import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from 'environments/environment';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { JwtService } from './jwt.service';
import { ToastService } from './toast.service';
import { GtmService } from '../../services/gtm/gtm.service';
import { filter, take } from 'rxjs/operators';

@Injectable()
export class MicrofrontendsService {
  microfrontends: any = {};
  isBootstraped: boolean = false;
  private microStatus = {};

  constructor(
    public translate: TranslateService,
    public jwtService: JwtService,
    private toastService: ToastService,
    private gtmService: GtmService
  ) {}

  bootstrap(): void {
    this.isBootstraped = false;
    this.microfrontends = {};
    if (environment.microfrontends) {
      document.addEventListener(environment.sids.admin, (event: any) => this.onMicrofrontendEvent(event.detail));
      Object.keys(environment.microfrontends)
        .filter((micro) => environment.microfrontends[micro].active)
        .forEach((micro) => this.loadMicrofrontend(micro, environment.microfrontends[micro]));
    }
  }

  loadMicrofrontend(microfrontendName: string, microfrontendConfig: any): BehaviorSubject<boolean> {
    const subject = this.getMicrofrontendStatus(microfrontendName);
    this.microfrontends[microfrontendName] = false;
    if (this.isAllowed(microfrontendConfig)) {
      const scriptElement = document.createElement('script');
      scriptElement.id = microfrontendName;
      scriptElement.setAttribute('data-microfrontend', microfrontendName);
      scriptElement.setAttribute('src', microfrontendConfig.script);
      scriptElement.onload = () => {
        this.bootstrapMicrofrontend(microfrontendName, microfrontendConfig);
      };
      document.body.appendChild(scriptElement);
    }
    return subject;
  }

  bootstrapMicrofrontend(microfrontendName: string, microfrontendConfig: any): BehaviorSubject<boolean> {
    const subject = this.getMicrofrontendStatus(microfrontendName);
    subject.next(false);
    this.fireMicrofrontendEvent(microfrontendName, 'bootstrap', {
      locale: this.translate.currentLang,
      serviceId: environment.sids.admin,
      apiKey: microfrontendConfig.authorization ? this.jwtService.getApiKey() : null,
      ...microfrontendConfig
    });
    return subject;
  }

  refreshAuthorization() {
    if (environment.microfrontends) {
      Object.keys(environment.microfrontends)
        .filter((microfrontendName) => this.microfrontends[microfrontendName])
        .forEach((microfrontendName) => {
          const microfrontendConfig = environment.microfrontends[microfrontendName];
          if (microfrontendConfig.authorization && this.isAllowed(microfrontendConfig)) {
            this.bootstrapMicrofrontend(microfrontendName, microfrontendConfig);
          }
        });
    }
  }

  isReady(microfrontendName: string): boolean {
    return !!this.microfrontends[microfrontendName];
  }

  getMicrofrontendStatus(microfrontendName: string): BehaviorSubject<boolean> {
    const subject = this.microStatus[microfrontendName]
      ? this.microStatus[microfrontendName]
      : new BehaviorSubject<boolean>(false);
    this.microStatus[microfrontendName] = subject;
    return subject;
  }

  fireMicrofrontendEvent(microfrontendName: string, action: string, payload?: any): void {
    if (microfrontendName) {
      document.dispatchEvent(new CustomEvent(microfrontendName, { detail: { action: action, payload: payload } }));
    }
  }

  onMicrofrontendEvent(detail: any): void {
    const { microfrontendName, action } = detail;
    switch (action) {
      case 'ready': {
        this.isBootstraped = true;
        this.microfrontends[microfrontendName] = true;
        this.microStatus[microfrontendName]?.next(true);
        break;
      }
      case 'clicked': {
        if (microfrontendName === 'GlCookies') {
          this.gtmService.setCookiesConfiguredAndNotifyGTM();
        }
        break;
      }
      case 'apiError': {
        const { config, error } = detail.payload;
        const isExpiredToken = this.jwtService.isExpired(config.apiKey);
        if (error.status === 401 || (error.status === 0 && isExpiredToken)) {
          error.serverMessage = 'error.sessionExpired';
          location.reload();
        }
        this.toastService.showError(error, '', true);
      }
    }
  }

  /**
   * Check if microfrontend is loaded and load it
   * @param microFrontendName
   */
  public hasMicrofrontend(microFrontendName: string): Promise<boolean> {
    const config = environment.microfrontends[microFrontendName];
    const microStatus = this.isReady(microFrontendName)
      ? this.bootstrapMicrofrontend(microFrontendName, config)
      : this.loadMicrofrontend(microFrontendName, config);

    return lastValueFrom(
      microStatus.pipe(
        filter((ready) => ready),
        take(1)
      )
    );
  }

  private isAllowed(microfrontendConfig) {
    if (!microfrontendConfig || !microfrontendConfig.script) return false;
    return true;
  }
}
